import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Api as ApiIcon,
  Storage as StorageIcon,
  CloudSync as CloudSyncIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  PlayArrow as TestIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../config';
import GoogleMCPNavigation from '../components/GoogleMCPNavigation';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface GoogleCredentials {
  google_ads_customer_id: string;
  google_ads_developer_token: string;
  google_ads_client_id: string;
  google_ads_client_secret: string;
  google_ads_refresh_token: string;
  google_analytics_property_id: string;
  google_analytics_credentials_json: string;
}

interface SystemSettings {
  simulation_mode: boolean;
  auto_sync_enabled: boolean;
  sync_interval_minutes: number;
  lead_score_threshold: number;
  max_daily_budget: number;
  default_campaign_status: string;
  notification_email: string;
  webhook_url: string;
  data_retention_days: number;
  enable_debug_logs: boolean;
}

interface SystemStatus {
  google_ads_connected: boolean;
  google_analytics_connected: boolean;
  database_connected: boolean;
  last_sync: string;
  total_campaigns: number;
  total_leads: number;
  system_health: 'healthy' | 'warning' | 'error';
}

const GoogleMCPSettings: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [credentials, setCredentials] = useState<GoogleCredentials>({
    google_ads_customer_id: '',
    google_ads_developer_token: '',
    google_ads_client_id: '',
    google_ads_client_secret: '',
    google_ads_refresh_token: '',
    google_analytics_property_id: '',
    google_analytics_credentials_json: ''
  });
  const [settings, setSettings] = useState<SystemSettings>({
    simulation_mode: false,
    auto_sync_enabled: true,
    sync_interval_minutes: 60,
    lead_score_threshold: 70,
    max_daily_budget: 1000,
    default_campaign_status: 'PAUSED',
    notification_email: '',
    webhook_url: '',
    data_retention_days: 90,
    enable_debug_logs: false
  });
  const [systemStatus, setSystemStatus] = useState<SystemStatus>({
    google_ads_connected: false,
    google_analytics_connected: false,
    database_connected: false,
    last_sync: '',
    total_campaigns: 0,
    total_leads: 0,
    system_health: 'warning'
  });
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState<{[key: string]: boolean}>({});
  const [testingConnection, setTestingConnection] = useState<{[key: string]: boolean}>({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });
  const [, setOpenBackupDialog] = useState(false);
  const [openResetDialog, setOpenResetDialog] = useState(false);

  const fetchSystemStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/google-mcp/system/status`);
      setSystemStatus(response.data.data);
    } catch (error: any) {
      console.error('Erro ao carregar status:', error);
      // Dados simulados para desenvolvimento
      setSystemStatus({
        google_ads_connected: true,
        google_analytics_connected: false,
        database_connected: true,
        last_sync: '2024-01-15T14:30:00Z',
        total_campaigns: 5,
        total_leads: 127,
        system_health: 'warning'
      });
    }
  }, []);

  const fetchCredentials = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/google-mcp/settings/credentials`);
      setCredentials(response.data.data);
    } catch (error: any) {
      console.error('Erro ao carregar credenciais:', error);
      showSnackbar('Erro ao carregar credenciais', 'error');
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/google-mcp/settings`);
      setSettings(response.data.data);
    } catch (error: any) {
      console.error('Erro ao carregar configurações:', error);
      showSnackbar('Erro ao carregar configurações', 'error');
    }
  }, []);

  useEffect(() => {
    fetchSystemStatus();
    fetchCredentials();
    fetchSettings();
  }, [fetchSystemStatus, fetchCredentials, fetchSettings]);

  const saveCredentials = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/google-mcp/settings/credentials`, credentials);
      showSnackbar('Credenciais salvas com sucesso!', 'success');
      fetchSystemStatus();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao salvar credenciais';
      showSnackbar(message, 'error');
    }
    setLoading(false);
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/google-mcp/settings`, settings);
      showSnackbar('Configurações salvas com sucesso!', 'success');
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao salvar configurações';
      showSnackbar(message, 'error');
    }
    setLoading(false);
  };

  const testConnection = async (service: 'google_ads' | 'google_analytics') => {
    setTestingConnection({ ...testingConnection, [service]: true });
    try {
      await axios.post(`${API_URL}/api/google-mcp/test-connection/${service}`);
      showSnackbar(`Conexão com ${service === 'google_ads' ? 'Google Ads' : 'Google Analytics'} testada com sucesso!`, 'success');
      fetchSystemStatus();
    } catch (error: any) {
      const message = error.response?.data?.detail || `Erro ao testar conexão com ${service}`;
      showSnackbar(message, 'error');
    }
    setTestingConnection({ ...testingConnection, [service]: false });
  };

  const syncData = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/google-mcp/sync`);
      showSnackbar('Sincronização iniciada com sucesso!', 'success');
      fetchSystemStatus();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao iniciar sincronização';
      showSnackbar(message, 'error');
    }
    setLoading(false);
  };

  const exportBackup = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/google-mcp/backup/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `google-mcp-backup-${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      showSnackbar('Backup exportado com sucesso!', 'success');
    } catch (error: any) {
      showSnackbar('Erro ao exportar backup', 'error');
    }
  };

  const resetSystem = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/google-mcp/system/reset`);
      showSnackbar('Sistema resetado com sucesso!', 'success');
      setOpenResetDialog(false);
      fetchSystemStatus();
      fetchCredentials();
      fetchSettings();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao resetar sistema';
      showSnackbar(message, 'error');
    }
    setLoading(false);
  };

  const togglePasswordVisibility = (field: string) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field]
    });
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusIcon = (connected: boolean) => {
    return connected ? (
      <CheckCircleIcon color="success" />
    ) : (
      <ErrorIcon color="error" />
    );
  };

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'healthy': return 'success';
      case 'warning': return 'warning';
      case 'error': return 'error';
      default: return 'info';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  return (
    <Container maxWidth="lg">
      {/* Navegação */}
      <GoogleMCPNavigation currentPage="settings" />
      
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Configurações Google MCP
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Gerencie credenciais, configurações e monitoramento do sistema
        </Typography>
      </Box>

      {/* Status do Sistema */}
      <Paper sx={{ mb: 4, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Status do Sistema</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchSystemStatus}
              size="small"
            >
              Atualizar
            </Button>
            <Button
              variant="contained"
              startIcon={<CloudSyncIcon />}
              onClick={syncData}
              disabled={loading}
              size="small"
            >
              Sincronizar
            </Button>
          </Box>
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getStatusIcon(systemStatus.google_ads_connected)}
                    <Typography sx={{ ml: 1 }}>Google Ads</Typography>
                  </Box>
                  <Chip 
                    label={systemStatus.google_ads_connected ? 'Conectado' : 'Desconectado'}
                    color={systemStatus.google_ads_connected ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getStatusIcon(systemStatus.google_analytics_connected)}
                    <Typography sx={{ ml: 1 }}>Google Analytics</Typography>
                  </Box>
                  <Chip 
                    label={systemStatus.google_analytics_connected ? 'Conectado' : 'Desconectado'}
                    color={systemStatus.google_analytics_connected ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary">Saúde do Sistema</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Chip 
                    label={systemStatus.system_health.toUpperCase()}
                    color={getHealthColor(systemStatus.system_health)}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary">Campanhas Ativas</Typography>
                <Typography variant="h6">{systemStatus.total_campaigns}</Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary">Total de Leads</Typography>
                <Typography variant="h6">{systemStatus.total_leads}</Typography>
              </CardContent>
            </Card>
          </Grid>
          
          {systemStatus.last_sync && (
            <Grid item xs={12}>
              <Alert severity="info">
                Última sincronização: {formatDate(systemStatus.last_sync)}
              </Alert>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Tabs de Configuração */}
      <Paper>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab icon={<SecurityIcon />} label="Credenciais" />
            <Tab icon={<SettingsIcon />} label="Configurações" />
            <Tab icon={<StorageIcon />} label="Backup & Reset" />
          </Tabs>
        </Box>

        {/* Tab 1: Credenciais */}
        <CustomTabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Credenciais das APIs
          </Typography>
          
          <Grid container spacing={3}>
            {/* Google Ads */}
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <ApiIcon sx={{ mr: 2 }} />
                    <Typography variant="h6">Google Ads API</Typography>
                    <Box sx={{ ml: 'auto', mr: 2 }}>
                      {getStatusIcon(systemStatus.google_ads_connected)}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Customer ID"
                        value={credentials.google_ads_customer_id}
                        onChange={(e) => setCredentials({ ...credentials, google_ads_customer_id: e.target.value })}
                        placeholder="123-456-7890"
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Developer Token"
                        type={showPasswords.developer_token ? 'text' : 'password'}
                        value={credentials.google_ads_developer_token}
                        onChange={(e) => setCredentials({ ...credentials, google_ads_developer_token: e.target.value })}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => togglePasswordVisibility('developer_token')}
                                edge="end"
                              >
                                {showPasswords.developer_token ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Client ID"
                        value={credentials.google_ads_client_id}
                        onChange={(e) => setCredentials({ ...credentials, google_ads_client_id: e.target.value })}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Client Secret"
                        type={showPasswords.client_secret ? 'text' : 'password'}
                        value={credentials.google_ads_client_secret}
                        onChange={(e) => setCredentials({ ...credentials, google_ads_client_secret: e.target.value })}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => togglePasswordVisibility('client_secret')}
                                edge="end"
                              >
                                {showPasswords.client_secret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Refresh Token"
                        type={showPasswords.refresh_token ? 'text' : 'password'}
                        value={credentials.google_ads_refresh_token}
                        onChange={(e) => setCredentials({ ...credentials, google_ads_refresh_token: e.target.value })}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => togglePasswordVisibility('refresh_token')}
                                edge="end"
                              >
                                {showPasswords.refresh_token ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                          variant="outlined"
                          startIcon={<TestIcon />}
                          onClick={() => testConnection('google_ads')}
                          disabled={testingConnection.google_ads}
                        >
                          {testingConnection.google_ads ? 'Testando...' : 'Testar Conexão'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
            
            {/* Google Analytics */}
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <ApiIcon sx={{ mr: 2 }} />
                    <Typography variant="h6">Google Analytics API</Typography>
                    <Box sx={{ ml: 'auto', mr: 2 }}>
                      {getStatusIcon(systemStatus.google_analytics_connected)}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Property ID"
                        value={credentials.google_analytics_property_id}
                        onChange={(e) => setCredentials({ ...credentials, google_analytics_property_id: e.target.value })}
                        placeholder="123456789"
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Service Account JSON"
                        multiline
                        rows={4}
                        value={credentials.google_analytics_credentials_json}
                        onChange={(e) => setCredentials({ ...credentials, google_analytics_credentials_json: e.target.value })}
                        placeholder="Cole aqui o conteúdo do arquivo JSON da conta de serviço"
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                          variant="outlined"
                          startIcon={<TestIcon />}
                          onClick={() => testConnection('google_analytics')}
                          disabled={testingConnection.google_analytics}
                        >
                          {testingConnection.google_analytics ? 'Testando...' : 'Testar Conexão'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={saveCredentials}
                  disabled={loading}
                >
                  {loading ? 'Salvando...' : 'Salvar Credenciais'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CustomTabPanel>

        {/* Tab 2: Configurações */}
        <CustomTabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Configurações do Sistema
          </Typography>
          
          <Grid container spacing={3}>
            {/* Configurações Gerais */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardHeader title="Configurações Gerais" />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.simulation_mode}
                            onChange={(e) => setSettings({ ...settings, simulation_mode: e.target.checked })}
                          />
                        }
                        label="Modo Simulação"
                      />
                      <Typography variant="caption" color="text.secondary" display="block">
                        Ativa o modo de simulação para testes
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.auto_sync_enabled}
                            onChange={(e) => setSettings({ ...settings, auto_sync_enabled: e.target.checked })}
                          />
                        }
                        label="Sincronização Automática"
                      />
                      <Typography variant="caption" color="text.secondary" display="block">
                        Sincroniza dados automaticamente
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Intervalo de Sincronização (minutos)"
                        type="number"
                        value={settings.sync_interval_minutes}
                        onChange={(e) => setSettings({ ...settings, sync_interval_minutes: parseInt(e.target.value) })}
                        disabled={!settings.auto_sync_enabled}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Status Padrão de Campanhas</InputLabel>
                        <Select
                          value={settings.default_campaign_status}
                          label="Status Padrão de Campanhas"
                          onChange={(e) => setSettings({ ...settings, default_campaign_status: e.target.value })}
                        >
                          <MenuItem value="ENABLED">Ativa</MenuItem>
                          <MenuItem value="PAUSED">Pausada</MenuItem>
                          <MenuItem value="REMOVED">Removida</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Configurações de Leads */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardHeader title="Configurações de Leads" />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Score Mínimo de Lead"
                        type="number"
                        value={settings.lead_score_threshold}
                        onChange={(e) => setSettings({ ...settings, lead_score_threshold: parseInt(e.target.value) })}
                        inputProps={{ min: 0, max: 100 }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Orçamento Diário Máximo (R$)"
                        type="number"
                        value={settings.max_daily_budget}
                        onChange={(e) => setSettings({ ...settings, max_daily_budget: parseFloat(e.target.value) })}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Configurações de Notificação */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardHeader title="Configurações de Notificação" />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Email de Notificação"
                        type="email"
                        value={settings.notification_email}
                        onChange={(e) => setSettings({ ...settings, notification_email: e.target.value })}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="URL do Webhook"
                        value={settings.webhook_url}
                        onChange={(e) => setSettings({ ...settings, webhook_url: e.target.value })}
                        placeholder="https://seu-webhook.com/endpoint"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Configurações Avançadas */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardHeader title="Configurações Avançadas" />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Retenção de Dados (dias)"
                        type="number"
                        value={settings.data_retention_days}
                        onChange={(e) => setSettings({ ...settings, data_retention_days: parseInt(e.target.value) })}
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.enable_debug_logs}
                            onChange={(e) => setSettings({ ...settings, enable_debug_logs: e.target.checked })}
                          />
                        }
                        label="Logs de Debug"
                      />
                      <Typography variant="caption" color="text.secondary" display="block">
                        Ativa logs detalhados para debug
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={saveSettings}
                  disabled={loading}
                >
                  {loading ? 'Salvando...' : 'Salvar Configurações'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CustomTabPanel>

        {/* Tab 3: Backup & Reset */}
        <CustomTabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Backup e Reset do Sistema
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardHeader title="Backup de Dados" />
                <CardContent>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Exporte todas as configurações e dados do sistema para backup.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={exportBackup}
                      color="primary"
                    >
                      Exportar Backup
                    </Button>
                    
                    <Button
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      onClick={() => setOpenBackupDialog(true)}
                    >
                      Importar Backup
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardHeader title="Reset do Sistema" />
                <CardContent>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Reseta todas as configurações para os valores padrão.
                  </Typography>
                  
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <strong>Atenção:</strong> Esta ação não pode ser desfeita!
                  </Alert>
                  
                  <Button
                    variant="contained"
                    startIcon={<DeleteIcon />}
                    onClick={() => setOpenResetDialog(true)}
                    color="error"
                  >
                    Reset Sistema
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardHeader title="Informações do Sistema" />
                <CardContent>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <InfoIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Versão do Sistema"
                        secondary="Google MCP v1.0.0"
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        <StorageIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Banco de Dados"
                        secondary={systemStatus.database_connected ? 'Conectado' : 'Desconectado'}
                      />
                      <ListItemSecondaryAction>
                        {getStatusIcon(systemStatus.database_connected)}
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        <CloudSyncIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Última Sincronização"
                        secondary={systemStatus.last_sync ? formatDate(systemStatus.last_sync) : 'Nunca'}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CustomTabPanel>
      </Paper>

      {/* Dialog de Confirmação de Reset */}
      <Dialog open={openResetDialog} onClose={() => setOpenResetDialog(false)}>
        <DialogTitle>Confirmar Reset do Sistema</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>ATENÇÃO:</strong> Esta ação irá:
          </Alert>
          <Typography component="div">
            <ul>
              <li>Resetar todas as configurações para valores padrão</li>
              <li>Limpar todas as credenciais salvas</li>
              <li>Remover dados de cache</li>
              <li>Manter apenas os dados essenciais do banco</li>
            </ul>
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Esta ação não pode ser desfeita. Certifique-se de ter um backup antes de continuar.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResetDialog(false)}>Cancelar</Button>
          <Button 
            onClick={resetSystem} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Resetando...' : 'Confirmar Reset'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default GoogleMCPSettings;
